/**
 * Reusable draggable scrollbar for Pixi.js panels.
 * Supports mouse-wheel sync and pointer-drag for mobile.
 */
import type { FederatedPointerEvent } from 'pixi.js';
import { Container, Graphics } from 'pixi.js';
import { darken, lighten } from './PanelUtils';

// ─── Visual constants ──────────────────────────────────────
const BAR_WIDTH     = 10;
/** Invisible touch padding around the thumb (px each side). WCAG 44x44 target. */
const THUMB_TOUCH_PAD = 12;
const MIN_THUMB_H   = 20;
const TRACK_COLOR   = 0x080a10;
const TRACK_BORDER  = 0x1a2030;
const THUMB_COLOR   = 0x334455;
const THUMB_HOVER   = 0x445566;
const THUMB_ACTIVE  = 0x556677;
const THUMB_RADIUS  = 3;
const TRACK_RADIUS  = 3;

export interface ScrollBarOptions {
  /** Height of the visible scrollbar track in px. */
  trackHeight: number;
  /** Total height of the scrollable content in px. */
  contentHeight: number;
  /** Height of the visible content area in px (same as trackHeight usually). */
  visibleHeight: number;
  /** Called when the user drags the scrollbar. `fraction` is 0–1. */
  onChange: (fraction: number) => void;
}

export interface ScrollBarHandle {
  /** The display container — add this to your panel. */
  container: Container;
  /**
   * Sync the scrollbar thumb to an external scroll position.
   * `fraction` is 0–1 (0 = top, 1 = fully scrolled).
   */
  setFraction: (fraction: number) => void;
  /** Update content/visible height when content changes (e.g. after rebuild). */
  updateSizes: (contentHeight: number, visibleHeight: number) => void;
  /** Clean up event listeners. */
  destroy: () => void;
}

/**
 * Create a scrollbar at position (0, 0) in its own container.
 * Caller positions the container via `container.position.set(x, y)`.
 */
export function createScrollBar(opts: ScrollBarOptions): ScrollBarHandle {
  const { trackHeight, onChange } = opts;
  let { contentHeight, visibleHeight } = opts;

  const root = new Container();
  const track = new Graphics();
  const thumb = new Graphics();

  root.addChild(track);
  root.addChild(thumb);

  let fraction = 0;
  let dragging = false;
  let dragStartY = 0;
  let dragStartFraction = 0;

  function thumbHeight(): number {
    if (contentHeight <= visibleHeight) return trackHeight;
    return Math.max(MIN_THUMB_H, (visibleHeight / contentHeight) * trackHeight);
  }

  function maxThumbY(): number {
    return trackHeight - thumbHeight();
  }

  function drawTrack() {
    track.clear();
    // Inset track background
    track.roundRect(0, 0, BAR_WIDTH, trackHeight, TRACK_RADIUS);
    track.fill(TRACK_COLOR);
    // Top inset shadow
    track.moveTo(TRACK_RADIUS, 0.5);
    track.lineTo(BAR_WIDTH - TRACK_RADIUS, 0.5);
    track.stroke({ color: darken(TRACK_COLOR, 0.25), width: 1, alpha: 0.7 });
    // Bottom highlight
    track.moveTo(TRACK_RADIUS, trackHeight - 0.5);
    track.lineTo(BAR_WIDTH - TRACK_RADIUS, trackHeight - 0.5);
    track.stroke({ color: lighten(TRACK_COLOR, 0.12), width: 1, alpha: 0.5 });
    // Border
    track.roundRect(0, 0, BAR_WIDTH, trackHeight, TRACK_RADIUS);
    track.stroke({ color: TRACK_BORDER, width: 1, alpha: 0.5 });
  }

  function drawThumb(color: number) {
    const th = thumbHeight();
    const ty = fraction * maxThumbY();
    // Invisible expanded hit area (kept in sync with current thumb Y/height)
    thumb.hitArea = {
      contains(x, y) {
        return x >= -THUMB_TOUCH_PAD && x <= BAR_WIDTH + THUMB_TOUCH_PAD
            && y >= ty - THUMB_TOUCH_PAD && y <= ty + th + THUMB_TOUCH_PAD;
      },
    };
    thumb.clear();
    // Body
    thumb.roundRect(1, ty, BAR_WIDTH - 2, th, THUMB_RADIUS);
    thumb.fill(color);
    // Top highlight
    thumb.moveTo(THUMB_RADIUS + 1, ty + 0.5);
    thumb.lineTo(BAR_WIDTH - THUMB_RADIUS - 1, ty + 0.5);
    thumb.stroke({ color: lighten(color, 0.3), width: 1, alpha: 0.6 });
    // Bottom shadow
    thumb.moveTo(THUMB_RADIUS + 1, ty + th - 0.5);
    thumb.lineTo(BAR_WIDTH - THUMB_RADIUS - 1, ty + th - 0.5);
    thumb.stroke({ color: darken(color, 0.2), width: 1, alpha: 0.5 });
    // Center grip lines (3 horizontal dashes)
    const midY = ty + th / 2;
    const gripColor = lighten(color, 0.15);
    if (th > 30) {
      for (let i = -1; i <= 1; i++) {
        const gy = midY + i * 3;
        thumb.moveTo(3, gy);
        thumb.lineTo(BAR_WIDTH - 3, gy);
        thumb.stroke({ color: gripColor, width: 1, alpha: 0.4 });
      }
    }
  }

  function redraw() {
    const needsScroll = contentHeight > visibleHeight;
    root.visible = needsScroll;
    if (!needsScroll) return;
    drawTrack();
    drawThumb(dragging ? THUMB_ACTIVE : THUMB_COLOR);
  }

  // ── Track click (jump to position) ──────────────────────
  track.eventMode = 'static';
  track.cursor = 'pointer';
  track.on('pointerdown', (e: FederatedPointerEvent) => {
    const localY = e.getLocalPosition(root).y;
    const th = thumbHeight();
    fraction = Math.max(0, Math.min(1, (localY - th / 2) / maxThumbY()));
    redraw();
    onChange(fraction);
  });

  // ── Thumb drag ──────────────────────────────────────────
  thumb.eventMode = 'static';
  thumb.cursor = 'grab';

  thumb.on('pointerdown', (e: FederatedPointerEvent) => {
    dragging = true;
    dragStartY = e.global.y;
    dragStartFraction = fraction;
    thumb.cursor = 'grabbing';
    drawThumb(THUMB_ACTIVE);
    e.stopPropagation();
  });

  thumb.on('pointerenter', () => {
    if (!dragging) drawThumb(THUMB_HOVER);
  });
  thumb.on('pointerleave', () => {
    if (!dragging) drawThumb(THUMB_COLOR);
  });

  // Global drag tracking via Pixi v8 global events on the thumb
  thumb.on('globalpointermove', (e: FederatedPointerEvent) => {
    if (!dragging) return;
    const dy = e.global.y - dragStartY;
    const maxY = maxThumbY();
    if (maxY <= 0) return;
    fraction = Math.max(0, Math.min(1, dragStartFraction + dy / maxY));
    drawThumb(THUMB_ACTIVE);
    onChange(fraction);
  });

  thumb.on('pointerup', () => {
    if (!dragging) return;
    dragging = false;
    thumb.cursor = 'grab';
    drawThumb(THUMB_COLOR);
  });

  thumb.on('pointerupoutside', () => {
    if (!dragging) return;
    dragging = false;
    thumb.cursor = 'grab';
    drawThumb(THUMB_COLOR);
  });

  // Initial draw
  redraw();

  return {
    container: root,

    setFraction(f: number) {
      fraction = Math.max(0, Math.min(1, f));
      redraw();
    },

    updateSizes(newContentH: number, newVisibleH: number) {
      contentHeight = newContentH;
      visibleHeight = newVisibleH;
      fraction = Math.max(0, Math.min(1, fraction));
      redraw();
    },

    destroy() {
      root.destroy({ children: true });
    },
  };
}

/** Width of the scrollbar — use for layout calculations. */
export { BAR_WIDTH as SCROLLBAR_WIDTH };
